import { UNIT_ALIASES, unitFromWord, type Unit } from './units.ts';

/** One ingredient line split into its parts (PLAN.md §5 step 4); `raw` is always kept. */
export interface ParsedIngredient {
  raw: string;
  quantity: number | null;
  /** Upper end of a range ("2–3"). */
  quantityMax: number | null;
  unit: Unit | null;
  name: string;
  note: string | null;
  optional: boolean;
}

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 1 / 2,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '¼': 1 / 4,
  '¾': 3 / 4,
  '⅕': 1 / 5,
  '⅖': 2 / 5,
  '⅗': 3 / 5,
  '⅘': 4 / 5,
  '⅙': 1 / 6,
  '⅚': 5 / 6,
  '⅛': 1 / 8,
  '⅜': 3 / 8,
  '⅝': 5 / 8,
  '⅞': 7 / 8,
};
const FRACTION_CHARS = Object.keys(UNICODE_FRACTIONS).join('');

const NUMBER_WORDS: Record<string, number> = {
  ein: 1,
  eine: 1,
  einen: 1,
  einem: 1,
  einer: 1,
  eins: 1,
  zwei: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  zwölf: 12,
  halbe: 0.5,
  halben: 0.5,
  halber: 0.5,
  halbes: 0.5,
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  twelve: 12,
  half: 0.5,
  dozen: 12,
};

/** One number: `1`, `1,5`, `1.5`, `1/2`, `½`, `1 ½`, `1½`, `1 1/2`. */
const NUM = `(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:[.,]\\d+)?(?:\\s*[${FRACTION_CHARS}])?|[${FRACTION_CHARS}])`;
const RANGE_SEP = `\\s*(?:-|–|—|bis|to|or|oder)\\s*`;
const QUANTITY = new RegExp(`^(${NUM})(?:${RANGE_SEP}(${NUM}))?`, 'i');

const ESCAPED_ALIASES = UNIT_ALIASES.map((a) => a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
/** A unit right after the quantity, with or without a space ("200g", "2 EL", "1 Tl."). */
const UNIT = new RegExp(`^(${ESCAPED_ALIASES.join('|')})(?:\\.|(?=[\\s,;:)(/]|$))`, 'i');

/** Words between quantity and unit or name that describe the amount, not the ingredient. */
const MODIFIER =
  /^(gestrichene[rn]?|gestr\.|gehäufte[rn]?|geh\.|knapp(?:e[rn]?)?|gute[rn]?|kleine[rn]?|große[rn]?|großer|mittelgroße[rn]?|mittlere[rn]?|level|heaped|heaping|rounded|scant|generous|large|small|medium|big)\s+/i;

const OPTIONAL = /\b(?:optional|opt\.|nach belieben|if desired|falls vorhanden)\b/i;
const TO_TASTE = /\b(?:nach geschmack|zum abschmecken|to taste|as needed|nach bedarf)\b/i;
const VAGUE = /^(etwas|wenig|einige|ein paar|some|a few|a little|a bit of|a pinch of)\s+/i;
const BULLET = /^(?:[-–—•*·▪◦]|\d+[.)](?=\s))\s*/;

export function parseNumber(text: string): number | null {
  const t = text.trim();
  const unicode = new RegExp(`^(\\d+)?\\s*([${FRACTION_CHARS}])$`).exec(t);
  if (unicode?.[2]) return Number(unicode[1] ?? 0) + (UNICODE_FRACTIONS[unicode[2]] ?? 0);
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const fraction = /^(\d+)\/(\d+)$/.exec(t);
  if (fraction) return Number(fraction[2]) === 0 ? null : Number(fraction[1]) / Number(fraction[2]);
  // "1.000" (German thousands) vs "1.5": three digits after a dot mean thousands.
  if (/^\d{1,3}\.\d{3}$/.test(t)) return Number(t.replace('.', ''));
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const round = (n: number) => Math.round(n * 1000) / 1000;

/**
 * The editor's amount field ("200", "1,5", "½", "1 1/2", "2–3"): the quantity and its range's
 * upper end, both null when the field is empty; null when the text isn't an amount.
 */
export function parseAmountText(
  text: string,
): { quantity: number | null; quantityMax: number | null } | null {
  const t = text.trim();
  if (!t) return { quantity: null, quantityMax: null };
  const [lo, hi, ...rest] = t.split(/\s*(?:-|–|—|\bbis\b|\bto\b)\s*/i);
  if (rest.length > 0 || lo === undefined) return null;
  const quantity = parseNumber(lo);
  const quantityMax = hi === undefined ? null : parseNumber(hi);
  if (quantity === null || quantity < 0 || (hi !== undefined && quantityMax === null)) return null;
  if (quantityMax !== null && quantityMax <= quantity) return null;
  return {
    quantity: round(quantity),
    quantityMax: quantityMax === null ? null : round(quantityMax),
  };
}

/** An amount back to the editor's field: plain decimals in the locale ("1,5", "2–3"). */
export function amountText(
  amount: { quantity: number | null; quantityMax?: number | null },
  locale: 'de' | 'en' = 'de',
): string {
  const number = (n: number) => {
    const plain = String(round(n));
    return locale === 'de' ? plain.replace('.', ',') : plain;
  };
  if (amount.quantity === null) return '';
  const max = amount.quantityMax ?? null;
  return max === null ? number(amount.quantity) : `${number(amount.quantity)}–${number(max)}`;
}

function joinNotes(notes: (string | null | undefined)[]): string | null {
  const parts = notes.map((n) => n?.trim()).filter((n): n is string => !!n);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** Quantity + unit at the start of `text`, with what's left. */
function leadingAmount(text: string): {
  quantity: number | null;
  quantityMax: number | null;
  unit: Unit | null;
  rest: string;
  modifier: string | null;
} {
  let rest = text;
  let quantity: number | null = null;
  let quantityMax: number | null = null;
  let modifier: string | null = null;

  const q = QUANTITY.exec(rest);
  if (q?.[1]) {
    quantity = parseNumber(q[1]);
    quantityMax = q[2] ? parseNumber(q[2]) : null;
    rest = rest.slice(q[0].length);
    // "2 x 400 g": a multiplier; the amount is the product.
    const times = new RegExp(`^\\s*[x×]\\s*(${NUM})`, 'i').exec(rest);
    const factor = times?.[1] ? parseNumber(times[1]) : null;
    if (times && factor !== null && quantity !== null) {
      quantity *= factor;
      quantityMax = quantityMax === null ? null : quantityMax * factor;
      rest = rest.slice(times[0].length);
    }
  } else {
    const word = /^([a-zäöüß]+)\s+/i.exec(rest);
    const value = word?.[1] ? NUMBER_WORDS[word[1].toLowerCase()] : undefined;
    if (word && value !== undefined) {
      quantity = value;
      rest = rest.slice(word[0].length);
    }
  }
  rest = rest.trimStart();

  const mod = MODIFIER.exec(rest);
  if (quantity !== null && mod?.[1]) {
    modifier = mod[1];
    rest = rest.slice(mod[0].length);
  }

  let unit: Unit | null = null;
  if (
    quantity !== null ||
    /^(prise|msp|schuss|spritzer|pinch|dash|handvoll|handful)\b/i.test(rest)
  ) {
    const u = UNIT.exec(rest);
    if (u?.[1]) {
      const candidate = unitFromWord(u[1]);
      // A single-letter alias ("c", "l") only counts when a quantity precedes it and it
      // isn't the start of a word.
      if (candidate && (u[1].length > 1 || quantity !== null)) {
        unit = candidate;
        rest = rest.slice(u[0].length).trimStart();
      }
    }
  }
  rest = rest.replace(/^(?:of|von)\s+/i, '');
  // "2 große Zwiebeln": a size word after the unit.
  const mod2 = MODIFIER.exec(rest);
  if (mod2?.[1] && !modifier) {
    modifier = mod2[1];
    rest = rest.slice(mod2[0].length);
  }
  return { quantity, quantityMax, unit, rest, modifier };
}

/**
 * Parses one ingredient line in German or English: quantities (`1,5`, `½`, `1 ½`, `1/2`,
 * words), ranges (`2–3`, `2 bis 3`), units (g, EL, TL, cup, Prise, Bund, Dose, Zehe …, glued or
 * not), notes (after a comma or in parentheses) and optional markers. Never throws; anything it
 * can't read stays in `name`.
 */
export function parseIngredientLine(line: string): ParsedIngredient {
  const raw = line.trim();
  let text = raw.replace(BULLET, '').replace(/\s+/g, ' ').trim();
  const notes: string[] = [];
  let optional = false;

  // Parentheticals are notes ("(à 400 g)", "(optional)").
  text = text
    .replace(/\(([^)]*)\)/g, (_, inner: string) => {
      const note = inner.trim();
      if (OPTIONAL.test(note) && note.replace(OPTIONAL, '').trim() === '') optional = true;
      else if (note) notes.push(note);
      return ' ';
    })
    .replace(/\s+/g, ' ')
    .trim();

  if (OPTIONAL.test(text)) {
    optional = true;
    text = text
      .replace(OPTIONAL, '')
      .replace(/\s*,\s*(?=,|$)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const taste = TO_TASTE.exec(text);
  if (taste) {
    notes.unshift(taste[0]);
    text = text
      .replace(TO_TASTE, '')
      .replace(/[\s,]+$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const vague = VAGUE.exec(text);
  if (vague?.[1]) {
    notes.unshift(vague[1]);
    text = text.slice(vague[0].length);
  }

  const lead = leadingAmount(text);
  const { modifier } = lead;
  let { quantity, quantityMax, unit, rest } = lead;

  // "Salz, 1 Prise" / "Mehl 200 g": amount after the name.
  if (quantity === null && unit === null) {
    const trailing = new RegExp(
      `^(.+?)[,:]?\\s+(${NUM}(?:${RANGE_SEP}${NUM})?\\s*\\S*)$`,
      'i',
    ).exec(text);
    if (trailing?.[1] && trailing[2]) {
      const amount = leadingAmount(trailing[2]);
      if (amount.quantity !== null && amount.rest === '') {
        ({ quantity, quantityMax, unit } = amount);
        rest = trailing[1];
      }
    }
  }

  // "Zwiebeln, fein gewürfelt": everything after the first comma is a note.
  let name = rest;
  const comma = /^([^,;]+)[,;]\s*(.*)$/.exec(rest);
  if (comma?.[1] !== undefined) {
    name = comma[1];
    if (comma[2]) notes.unshift(comma[2]);
  }
  if (modifier) notes.unshift(modifier);
  name = name.replace(/^[,;:\s-]+|[,;:\s]+$/g, '').trim();
  if (!name) name = raw;

  return {
    raw,
    quantity: quantity === null ? null : round(quantity),
    quantityMax: quantityMax === null ? null : round(quantityMax),
    unit,
    name,
    note: joinNotes(notes),
    optional,
  };
}

/**
 * A group header line ("Für die Sauce:", "Topping:", "## Teig"): returns its label, or null
 * for an ingredient line.
 */
export function parseGroupHeader(line: string): string | null {
  const text = line.trim();
  const markdown = /^#{1,6}\s+(.+)$/.exec(text);
  if (markdown?.[1]) return markdown[1].replace(/:$/, '').trim();
  const colon = /^([^\d:]{2,60}):$/.exec(text);
  if (colon?.[1]) return colon[1].trim();
  return null;
}

export interface ParsedIngredientLine extends ParsedIngredient {
  groupLabel: string | null;
}

/** Parses a block of ingredient text (the editor's textarea): one line each, headers group. */
export function parseIngredientText(text: string): ParsedIngredientLine[] {
  let group: string | null = null;
  const lines: ParsedIngredientLine[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const header = parseGroupHeader(line);
    if (header !== null) {
      group = header;
      continue;
    }
    lines.push({ ...parseIngredientLine(line), groupLabel: group });
  }
  return lines;
}
